import React, { use, useState } from 'react';
import { ShippingLabelCarrier, ShippingPickupType, ShippingPickupTypeEnum } from '@interfaces/models/timeline';
import clsx from 'clsx';
import { ShippingAddress } from '@interfaces/api/responses/user-shipping-address';
import { useTranslation, Trans } from 'next-i18next';
import { StepInfoContext } from '@context/step-info.context';
import RadioButton from '@components/atoms/radio-button/radio-button';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import styles from './select-carrier-options.module.scss';
import { DropOff } from './drop-off';

const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';

interface ShippingOptionProps {
  handleSelectCarrier: (carrier: ShippingLabelCarrier) => void;
  carrier: ShippingLabelCarrier;
  address: ShippingAddress | null;
  productId: string;
}

const ShippingOptions = ({ handleSelectCarrier, carrier, address, productId }: ShippingOptionProps) => {
  const { t } = useTranslation('orders-sales');

  const { userType } = use(StepInfoContext);

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const [showDropOff, setShowDropOff] = useState(false);

  const { dropOffLocationLink, vcDropOffMapAvailable, dropOffTypes = [] } = carrier || {};

  if (dropOffTypes.length === 0) {
    return null;
  }

  const onTypeChange = (pickUpType: ShippingPickupType) => {
    carrier.dropOffTypes = dropOffTypes.map((s) => ({ ...s, selected: s.type === pickUpType.type }));
    handleSelectCarrier({ ...carrier });
    sendAnalyticEvent('timeline_select_carrier_and_options_shipping_option_selection', { label: pickUpType.type });
  };

  const onDropOffClick = () => {
    const tracking = {
      label: userType,
      property: 'shipping_details',
    };
    if (vcDropOffMapAvailable) {
      sendAnalyticEvent('timeline_hyperlink', {
        action: 'clickable_text_drop_off_points_map_available',
        ...tracking,
      });
      setShowDropOff(true);
    } else {
      sendAnalyticEvent('timeline_hyperlink', {
        action: 'clickable_text_carrier_website',
        ...tracking,
      });
      window.open(dropOffLocationLink, '_blank');
    }
  };

  return (
    <>
      <p>{t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.SHIPPING_OPTION')}</p>

      {dropOffTypes.map((preference) => {
        const { selected, type } = preference;
        const isDropOff = type === ShippingPickupTypeEnum.dropOff;
        const isHomeCollect = type === ShippingPickupTypeEnum.homeCollection;

        return (
          <div
            key={type}
            className={clsx(styles.radioItem, {
              [styles.radioItemSelected]: selected,
            })}
          >
            <div className={styles.radioItem_header}>
              <RadioButton
                id={`shipping-type-${carrier.name}-${type}`}
                name={`shipping-type-${carrier.name}-${type}`}
                value={type}
                checked={selected}
                onChange={() => onTypeChange(preference)}
                label={
                  <p className={clsx({ [styles.radioItem_button_boldLabel]: selected })}>
                    {isDropOff
                      ? t(`${LANG_PREFIX}SHIPPING_OPTION.DROP_OFF`)
                      : t(`${LANG_PREFIX}SHIPPING_OPTION.PICK_UP`)}
                  </p>
                }
                position="left"
                className={clsx(styles.radioItem_button, {
                  [styles.radioItem_buttonClosed]: !selected,
                })}
              />
            </div>

            {selected && (
              <p>
                {isDropOff && (
                  <Trans
                    i18nKey={`${LANG_PREFIX}SHIPPING_OPTION.DROP_OFF_DESC`}
                    ns="orders-sales"
                    components={{
                      clickable:
                        vcDropOffMapAvailable || dropOffLocationLink ? (
                          <button
                            className={styles.radioItem_link}
                            onClick={onDropOffClick}
                          >
                            {''}
                          </button>
                        ) : (
                          <span />
                        ),
                    }}
                  />
                )}

                {isHomeCollect && t(`${LANG_PREFIX}SHIPPING_OPTION.PICK_UP_DESC`)}
              </p>
            )}
          </div>
        );
      })}

      {showDropOff && (
        <DropOff
          onClose={() => setShowDropOff(false)}
          address={address}
          carrierName={carrier.name || ''}
          carrierLogo={carrier.logo}
          productId={productId}
        />
      )}
    </>
  );
};

export default ShippingOptions;
